import {InteractionRequiredAuthError} from "@azure/msal-browser";
import {loginRequest, msalInstance} from "../msalConfig";

let cachedToken: string | null = null;
let cachedTokenExpiry: number | undefined = undefined;
let tokenAcquisitionPromise: Promise<string> | null = null;

export const clearTokenCache = () => {
    cachedToken = null;
    cachedTokenExpiry = undefined;
    tokenAcquisitionPromise = null;
};

/**
 * Acquires a fresh MSAL id token, reusing the module-level cache (60s expiry buffer) and
 * de-duping concurrent calls. Uses the shared {@link msalInstance} singleton, so it works
 * outside React — no hook, no MsalProvider context required.
 */
export const acquireToken = async (): Promise<string> => {
    if (tokenAcquisitionPromise) {
        return await tokenAcquisitionPromise;
    }

    tokenAcquisitionPromise = (async () => {
        if (cachedToken && cachedTokenExpiry && Date.now() < cachedTokenExpiry - 60000) {
            console.log('Using cached token. Will expire in ' + (cachedTokenExpiry - Date.now()) / 1000 + ' seconds')
            return cachedToken;
        }
        console.log('Fetching new token')

        return msalInstance.acquireTokenSilent({...loginRequest, account: msalInstance.getAllAccounts()[0]})
            .then((result) => {
                let activeAccount = msalInstance.getAllAccounts()[0];
                if (activeAccount === undefined || activeAccount === null) {
                    throw new InteractionRequiredAuthError('No account available');
                }
                cachedToken = result.idToken;
                cachedTokenExpiry = result.expiresOn?.getTime();
                return result.idToken;
            })
            .catch(async (error) => {
                if (error instanceof InteractionRequiredAuthError) {
                    try {
                        const resp = await msalInstance.acquireTokenPopup(loginRequest);
                        cachedToken = resp.idToken;
                        cachedTokenExpiry = resp.expiresOn?.getTime();
                        return resp.idToken;
                    } catch (err) {
                        console.log('Login unsuccessful', error);
                        await msalInstance.logout({postLogoutRedirectUri: '/'})
                        throw new Error('Login unsuccessful');
                    }
                } else {
                    await msalInstance.logout({postLogoutRedirectUri: '/'})
                    console.log('Unknown error', error);
                    throw new Error('Unknown error');
                }
            })
            .finally(() => {
                tokenAcquisitionPromise = null;
            });
    })();

    return await tokenAcquisitionPromise;
};

/** Runs a callback with a fresh bearer token. Plain function — usable from React or plain classes. */
export const execWithToken: ExecWithToken = async (callback) => {
    const token = await acquireToken();
    return callback(token);
};

/** Type of {@link execWithToken}: runs a callback with a fresh bearer token. */
export type ExecWithToken = <T>(callback: (token: string) => T | Promise<T>) => Promise<T>;

/** Backwards-compatible hook for React callers; simply returns the module-level {@link execWithToken}. */
const useExecWithToken = (): ExecWithToken => execWithToken;

export default useExecWithToken;
